import React, { useMemo } from 'react';
import {
  Grid,
  TextField,
} from '@mui/material';

const formInputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#7522D1',
  },
};

const LegalInfo = ({ formData, handleInputChange, errors = {}, touched = {} }) => {
  const validateField = (fieldName, value) => {
    const validators = {
      registrationNumber: (val) => {
        if (val && !/^[A-Za-z0-9\-\s]{5,30}$/.test(val)) {
          return 'Registration number must be 5-30 alphanumeric characters';
        }
        return null;
      },
      taxId: (val) => {
        if (val && !/^[A-Za-z0-9]{8,20}$/.test(val)) {
          return 'Tax ID must be 8-20 alphanumeric characters';
        }
        return null;
      },
      panNumber: (val) => {
        if (val && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val)) {
          return 'PAN must be in format: ABCDE1234F';
        }
        return null;
      },
      gstNumber: (val) => {
        if (val && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}[A-Z]{1}[0-9A-Z]{1}$/.test(val)) {
          return 'GST must be 15 characters in valid format';
        }
        return null;
      },
      cinNumber: (val) => {
        if (val && !/^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(val)) {
          return 'CIN must be 21 characters in valid format';
        }
        return null;
      },
      incorporationDate: (val) => {
        if (val) {
          const date = new Date(val);
          const today = new Date();
          if (isNaN(date.getTime()) || date > today) {
            return 'Please enter a valid date not in the future';
          }
        }
        return null;
      },
      legalStructure: (val) => {
        if (val && val.length > 500) {
          return 'Legal structure must not exceed 500 characters';
        }
        return null;
      }
    };

    return validators[fieldName] ? validators[fieldName](value) : null;
  };

  const fieldErrors = useMemo(() => {
    const errs = {};
    Object.keys(formData).forEach(key => {
      if (touched[key]) {
        const error = validateField(key, formData[key]);
        if (error) errs[key] = error;
      }
    });
    return errs;
  }, [formData, touched]);

  return (
    <Grid container spacing={3}>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Registration Number"
          value={formData.registrationNumber}
          onChange={(e) => handleInputChange('registrationNumber', e.target.value.toUpperCase())}
          variant="outlined"
          size="small"
          placeholder="REG123456"
          inputProps={{ maxLength: 30, style: { textTransform: 'uppercase' } }}
          error={!!errors.registrationNumber || !!fieldErrors.registrationNumber}
          helperText={errors.registrationNumber || fieldErrors.registrationNumber}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Tax ID"
          value={formData.taxId}
          onChange={(e) => handleInputChange('taxId', e.target.value.toUpperCase())}
          variant="outlined"
          size="small"
          placeholder="TAXID123456789"
          inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
          error={!!errors.taxId || !!fieldErrors.taxId}
          helperText={errors.taxId || fieldErrors.taxId}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="PAN Number"
          value={formData.panNumber}
          onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
          variant="outlined"
          size="small"
          placeholder="ABCDE1234F"
          inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
          error={!!errors.panNumber || !!fieldErrors.panNumber}
          helperText={errors.panNumber || fieldErrors.panNumber || 'Format: ABCDE1234F'}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="GST Number"
          value={formData.gstNumber}
          onChange={(e) => handleInputChange('gstNumber', e.target.value.toUpperCase())}
          variant="outlined"
          size="small"
          placeholder="12ABCDE1234F1Z1"
          inputProps={{ maxLength: 15, style: { textTransform: 'uppercase' } }}
          error={!!errors.gstNumber || !!fieldErrors.gstNumber}
          helperText={errors.gstNumber || fieldErrors.gstNumber || '15 characters'}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="CIN Number"
          value={formData.cinNumber}
          onChange={(e) => handleInputChange('cinNumber', e.target.value.toUpperCase())}
          variant="outlined"
          size="small"
          placeholder="U12345AB1234CDE123456"
          inputProps={{ maxLength: 21, style: { textTransform: 'uppercase' } }}
          error={!!errors.cinNumber || !!fieldErrors.cinNumber}
          helperText={errors.cinNumber || fieldErrors.cinNumber || '21 characters'}
          sx={formInputStyle}
        />
      </Grid>
      <Grid item size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Incorporation Date"
          value={formData.incorporationDate}
          onChange={(e) => handleInputChange('incorporationDate', e.target.value)}
          variant="outlined"
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: new Date().toISOString().split('T')[0] }}
          error={!!errors.incorporationDate || !!fieldErrors.incorporationDate}
          helperText={errors.incorporationDate || fieldErrors.incorporationDate}
          sx={formInputStyle}
        />
      </Grid>

      <Grid item size={{ xs: 12 }}>
        <TextField
          fullWidth
          label="Legal Structure"
          value={formData.legalStructure}
          onChange={(e) => handleInputChange('legalStructure', e.target.value)}
          variant="outlined"
          size="small"
          multiline
          rows={2}
          inputProps={{ maxLength: 500 }}
          helperText={errors.legalStructure || fieldErrors.legalStructure || `${formData.legalStructure?.length || 0}/500`}
          error={!!errors.legalStructure || !!fieldErrors.legalStructure}
          sx={formInputStyle}
        />
      </Grid>
    </Grid>
  );
};

export default LegalInfo;
